import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, UntypedFormControl } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs';
import { AgencyService } from 'src/app/services/agency.service';
import { LEPartnerService } from 'src/app/services/lepartner.service';
import { LookupService } from 'src/app/services/lookup.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { EventStepperDataService } from './event-stepper-data.service';

@Component({
  selector: 'ness-event-details',
  templateUrl: './event-details.component.html'
})
export class EventDetailsComponent implements OnInit, OnDestroy {
  defaultEventDate: Date = new Date();
  displaySidebar: boolean = false;
  eventDetailsFormGroup: FormGroup;
  eventLEPartnersFormControl: FormControl;
  eventPartnerForm: FormGroup = this.fb.group({
    LEPartnerID: [{ value: '', disabled: true }, NESSValidators.required]
  });
  maxDateRule: Date = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  openNewLEPartnerForm: boolean = false;
  openViewLEPartner: boolean = false;
  partnerFormTitleText: string = '';
  showOtherStatus: boolean = false;
  textAreaRows: number = AppConstants.defaultTextAreaRows;

  protected destroyed$: Subject<void> = new Subject();

  public filteredOutLEPartners: ServerModels.ENESS_LEPartnerDTO[] = [];

  private _filteredLEPartners: ServerModels.ENESS_LEPartnerDTO[] = [];
  private filteredLEPartners: BehaviorSubject<ServerModels.ENESS_LEPartnerDTO[]> = new BehaviorSubject<ServerModels.ENESS_LEPartnerDTO[]>(this._filteredLEPartners);
  public filteredLEPartners$: Observable<ServerModels.ENESS_LEPartnerDTO[]> = this.filteredLEPartners.asObservable();

  private _filteredAgencies: ServerModels.ENESS_REF_Agency[] = [];
  private filteredAgencies: BehaviorSubject<ServerModels.ENESS_REF_Agency[]> = new BehaviorSubject<ServerModels.ENESS_REF_Agency[]>(this._filteredAgencies);
  public filteredAgencies$: Observable<ServerModels.ENESS_REF_Agency[]> = this.filteredAgencies.asObservable();


  get lePartners(): ServerModels.ENESS_XREF_EventsLEPartnersDTO[] {
    return this.eventLEPartnersFormControl.value || [];
  }

  constructor(public controlContainer: FormGroupDirective,
    public eventDataSvc: EventStepperDataService,
    public lookupService: LookupService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private lePartnerService: LEPartnerService,
    private agencyService: AgencyService) { }

  ngOnInit() {
    this.defaultEventDate.setHours(0);
    this.defaultEventDate.setMinutes(0);
    this.defaultEventDate.setSeconds(0);
    this.defaultEventDate.setMilliseconds(0);
    this.eventDetailsFormGroup = <FormGroup>this.controlContainer.form;
    this.eventLEPartnersFormControl = <FormControl>this.eventDetailsFormGroup.get('ENESS_XREF_EventsLEPartners');

    this.filteredLEPartners$.pipe(takeUntil(this.destroyed$)).subscribe((p: ServerModels.ENESS_LEPartnerDTO[]) => {
      if (p.length < 1) {
        this.eventPartnerForm.disable();
      } else {
        this.eventPartnerForm.enable();
      }
    });

    this.formControlValueChanged();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private formControlValueChanged() {
    this.eventDetailsFormGroup.get('ENESS_XREF_LECaseNumbers.LECaseNumber').valueChanges.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(2000)
    ).subscribe((val:string) => {
      this.eventDataSvc.setEventLECaseNumberValue(val);
    });

    const otherStatus = this.eventDetailsFormGroup.get('OtherStatus');
    this.eventDetailsFormGroup.get('StatusID').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
      (status: string) => {
        this.showOtherStatus = !!status && status === this.eventDataSvc.otherStatusId;
        if (this.showOtherStatus) {
          otherStatus.setValidators([NESSValidators.required]);
          otherStatus.updateValueAndValidity();
        } else {
          otherStatus.clearValidators();
          otherStatus.setValue(null, false);
        }
      });

    const otherCrimeType = this.eventDetailsFormGroup.get('OtherCrimeType');
    this.eventDetailsFormGroup.get('CrimeTypeID').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
      (crimeType: string) => {
        if (!!crimeType && crimeType === this.eventDataSvc.otherCrimeTypeId) {
          otherCrimeType.setValidators([NESSValidators.required]);
          otherCrimeType.updateValueAndValidity();
        } else {
          otherCrimeType.reset();
          otherCrimeType.clearValidators();
          otherCrimeType.updateValueAndValidity();
        }
      });

    const agencyCtrl = this.eventDetailsFormGroup.get('ENESS_REF_Agency');
    const lePartnerCtrl = this.eventPartnerForm.get('LEPartnerID');
    agencyCtrl.get('AgencyID').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
      (agencyId: string) => {
        if (!!agencyId) {
          this._getFilteredLEPartners(agencyId);
        } else {
          lePartnerCtrl.reset({ value: '', disabled: true }, { onlySelf: true, emitEvent: false });
        }
      }
    );
    agencyCtrl.get('ENESS_REF_Counties.StateID').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
      (stateId: string) => {
        if (!!stateId) {
          this.agencyService.getAgenciesForState(stateId).pipe(take(1)).subscribe((agencies: ServerModels.ENESS_REF_Agency[]) => {
            this._filteredAgencies = agencies;
            this.filteredAgencies.next(this._filteredAgencies);
            agencyCtrl.get('AgencyID').enable({ onlySelf: true, emitEvent: false });
          });
        } else {
              agencyCtrl.reset({
                AgencyID: { value: '', disabled: true },
                AgencyName: '',
                CountyID: null,
                ENESS_REF_Counties: {
                  StateID: ''
                }
              }, { onlySelf: true, emitEvent: false });
              lePartnerCtrl.reset({ value: '', disabled: true }, { onlySelf: true, emitEvent: false });
        }
      });
  }

  public onAgencyChange(target: EventTarget) {
    let el: HTMLSelectElement = <HTMLSelectElement>target;
    let selectedAgency = <ServerModels.ENESS_REF_Agency>this._filteredAgencies[el.selectedIndex - 1];

    if (!!selectedAgency && !!Object.keys(selectedAgency).length) {
      const timeZoneCtrl = this.eventDetailsFormGroup.get('TimeZoneID');
      let timeZone: ServerModels.ENESS_REF_TimeZones = selectedAgency.ENESS_REF_Counties.ENESS_REF_TimeZone;
      timeZoneCtrl.setValue(selectedAgency.ENESS_REF_Counties.TimeZoneID);
      this.eventDetailsFormGroup.get('TimeZoneID').setValue(timeZone.TimeZoneID);
      this.eventDetailsFormGroup.get('TimeZoneName').setValue(timeZone.TimeZoneName);
      this.eventDetailsFormGroup.get('AgencyID').setValue(selectedAgency.AgencyID);
      this.eventDetailsFormGroup.get('ENESS_REF_Agency').patchValue({
        AgencyID: selectedAgency.AgencyID,
        AgencyName: selectedAgency.AgencyName,
        CountyID: selectedAgency.CountyID,
        ENESS_REF_Counties: {
          CountyID: selectedAgency.ENESS_REF_Counties.CountyID,
          StateID: selectedAgency.ENESS_REF_Counties.StateID
        }
      }, { onlySelf: false, emitEvent: false });
    }

    const lePartnerCtrl = this.eventPartnerForm.get('LEPartnerID');
    lePartnerCtrl.reset({ value: '', disabled: (!selectedAgency?.AgencyID) }, { onlySelf: false, emitEvent: false });
  }

  private _getFilteredLEPartners(agencyId: string): void {
    if (!agencyId)
      return;

    this.lePartnerService.getLEPartnerByAgencyId(agencyId).pipe(take(1)).subscribe({
      next: (lepartners: ServerModels.ENESS_LEPartnerDTO[]) => {
        let partners: ServerModels.ENESS_LEPartnerDTO[] = lepartners.filter((p, i) => {
          return (typeof (this.lePartners) !== 'undefined' && !!this.lePartners.length ? this.lePartners.findIndex(xp => xp.LEPartnerID == p.LEPartnerID) == -1 : true);
        });
        this._filteredLEPartners = partners;
        this.filteredLEPartners.next(this._filteredLEPartners);
        this.filteredOutLEPartners = lepartners.filter((p) => { return partners.indexOf(p) === -1 });
      },
      error: (err:any) => this.messageService.add({ key: 'eventFormMsg', severity: 'error', summary: 'Error:', detail: err.error })
    });
  }

  public onTimeZoneChange(target: EventTarget) {
    let el: HTMLSelectElement = <HTMLSelectElement>target;
    let tzId = el.value;
    if (!!tzId) {
      this.eventDetailsFormGroup.get('TimeZoneID').setValue(el.value);
      this.eventDetailsFormGroup.get('TimeZoneName').setValue(el.options[el.selectedIndex].label);
    }
  }

  public addLEPartnerToEvent(idx: number): void {
    if (idx <= 0)
      return;

    const selectedPartner = this._filteredLEPartners[idx - 1];
    if (!!selectedPartner) {
      if (!!selectedPartner.LEPartnerID) {
        let newLePartner: ServerModels.ENESS_XREF_EventsLEPartnersDTO = {
          EventLEPartnerStagingID: null,
          EventStagingID: this.eventDetailsFormGroup.get('EventStagingID').value,
          EventLEPartnerID: null,
          LEPartnerStagingID: selectedPartner.LEPartnerStagingID || null,
          EventID: this.eventDetailsFormGroup.get('EventID').value,
          LEPartnerID: selectedPartner.LEPartnerID,
          ENESS_LEPartner: selectedPartner
        } as ServerModels.ENESS_XREF_EventsLEPartnersDTO;

        this.eventDataSvc.copyEventLEPartnerToStaging(newLePartner).pipe(takeUntil(this.destroyed$)).subscribe((partner: ServerModels.ENESS_XREF_EventsLEPartnersDTO) => {
          this._filteredLEPartners.splice(idx - 1, 1);
          this.filteredLEPartners.next(this._filteredLEPartners);
          this.eventLEPartnersFormControl.setValue([...[partner], ...this.lePartners])
          this.eventPartnerForm.reset({
            LEPartnerID: ''
          });
        });
      }

      if (!selectedPartner.LEPartnerID && !!selectedPartner.LEPartnerStagingID) {
        this.onNewPartnerAdded(selectedPartner);
        this._filteredLEPartners.splice(idx - 1, 1);
        this.filteredLEPartners.next(this._filteredLEPartners);
      }

      if (this.filteredOutLEPartners.indexOf(selectedPartner) == -1) {
        this.filteredOutLEPartners.push(selectedPartner);
      }
    }
  }

  public addNewLEPartner(partnerDto?: ServerModels.ENESS_LEPartnerDTO): void {
    this.displaySidebar = true;
    this.openViewLEPartner = false;
    this.openNewLEPartnerForm = true;
    if (partnerDto && (!!partnerDto.LEPartnerID || !!partnerDto.LEPartnerStagingID)) {
      this.partnerFormTitleText = 'Edit LE Partner';
      this.eventDataSvc.setLEPartnerDetails(this.eventDataSvc.getLEPartnerDetailsValue());
    } else {
      this.partnerFormTitleText = 'Add New LE Partner';
      this.eventDataSvc.setLEPartnerDetails(this.lePartnerService.createEmptyLEPartner());
    }
  }

  public removeLEPartner(idx: number): void {
    this.eventDataSvc.deleteEventLEPartner(this.lePartners[idx].EventLEPartnerStagingID).pipe(takeUntil(this.destroyed$)).subscribe((result:number) => {
      let removedPartner = this.lePartners.splice(idx, 1);
      if (!!removedPartner[0].LEPartnerID) {
        let removedPartnerDTO = this.filteredOutLEPartners.find(s => s.LEPartnerID == removedPartner[0].LEPartnerID);
        this.filteredOutLEPartners.splice(this.filteredOutLEPartners.indexOf(removedPartnerDTO), 1);
        this._filteredLEPartners.push(removedPartnerDTO);
        this.filteredLEPartners.next(this._filteredLEPartners);
      }
      else if (!!removedPartner[0].ENESS_LEPartner) {
        let indexToRemove = this.filteredOutLEPartners.findIndex(x => x.LEPartnerStagingID == removedPartner[0].ENESS_LEPartner.LEPartnerStagingID)
        this.filteredOutLEPartners.splice(indexToRemove, 1);
        this._filteredLEPartners.push(removedPartner[0].ENESS_LEPartner);
        this.filteredLEPartners.next(this._filteredLEPartners);
      }
      this.eventLEPartnersFormControl.setValue([...this.lePartners])
    },
      (err:any) => {
        this.messageService.add({ key: 'eventLEPartnerMsg', severity: 'error', summary: 'Error:', detail: err.error });
      },
      () => {
        this._filteredLEPartners.sort(this._lePartnerCustomSort);
        this.filteredLEPartners.next(this._filteredLEPartners);
        this.eventPartnerForm.reset({ LEPartnerID: '' });
      });
  }

  public viewLEPartnerDetails(partnerStagingId: string) {
    this.eventDataSvc.getLEPartnerDetails(partnerStagingId).pipe(takeUntil(this.destroyed$)).subscribe((lePartner: ServerModels.ENESS_LEPartnerDTO) => {
      this.displaySidebar = true;
      this.openViewLEPartner = true;
      this.eventDataSvc.setLEPartnerDetails(lePartner);
    },
      (err:any) => {
        this.eventDataSvc.setLEPartnerDetails(null);
        this.messageService.add({ key: 'eventDetailMsg', severity: 'error', summary: 'Error:', detail: err.error });
        this.messageService.add({ key: 'eventLEPartnerMsg', severity: 'error', summary: 'Error:', detail: err.error });
      }
    );
  }

  public onLEPartnerClose(): void {
    this.eventDataSvc.setLEPartnerDetails(null);
    this.openViewLEPartner = false;
    this.openNewLEPartnerForm = false;
    this.displaySidebar = false;
  }

  public onNewPartnerAdded(partner: ServerModels.ENESS_LEPartnerDTO): void {
    if (!partner || !Object.keys(partner).length)
      return;

    let partnerIndex = this.lePartners.findIndex(x => !!partner.LEPartnerStagingID && x.LEPartnerStagingID.toLowerCase() === partner.LEPartnerStagingID.toLowerCase());

    let newEventLEPartner = <ServerModels.ENESS_XREF_EventsLEPartnersDTO>{
      EventLEPartnerStagingID: !!this.lePartners[partnerIndex] ? this.lePartners[partnerIndex].EventLEPartnerStagingID : null,
      EventStagingID: this.eventDetailsFormGroup.get('EventStagingID').value,
      EventLEPartnerID: !!this.lePartners[partnerIndex] ? this.lePartners[partnerIndex].EventLEPartnerID : null,
      LEPartnerStagingID: partner.LEPartnerStagingID,
      EventID: this.eventDetailsFormGroup.get('EventID').value,
      LEPartnerID: partner.LEPartnerID,
      FirstName: partner.FirstName,
      MiddleName: partner.MiddleName,
      LastName: partner.LastName,
      ENESS_LEPartner: partner
    };

    this.eventDataSvc.addOrUpdateEventLEPartner(newEventLEPartner).pipe(takeUntil(this.destroyed$)).subscribe((newPartner: ServerModels.ENESS_XREF_EventsLEPartnersDTO) => {
      if (partnerIndex === -1) {
        this.eventLEPartnersFormControl.setValue([newPartner, ...this.lePartners]);
      } else {
        this.lePartners[partnerIndex] = { ...newPartner };
      }

      if (this.filteredOutLEPartners.findIndex(x => x.LEPartnerStagingID == newPartner.ENESS_LEPartner.LEPartnerStagingID) == -1) {
        this.filteredOutLEPartners.push(newPartner.ENESS_LEPartner);
      }

      this.lePartners.sort(this._lePartnerCustomSort);
      this.eventLEPartnersFormControl.setValue([...this.lePartners]);
      this.openNewLEPartnerForm = false;
      this.displaySidebar = false;

    },
      (err:any) => {
        this.messageService.add({ key: 'lePartnerFormMsg', severity: 'error', summary: 'Error:', detail: err.error });
        this.messageService.add({ key: 'eventLEPartnerMsg', severity: 'error', summary: 'Error:', detail: err.error });
      }
    );
  }

  private _lePartnerCustomSort(p1: ServerModels.ENESS_XREF_EventsLEPartnersDTO | ServerModels.ENESS_LEPartnerDTO, p2: ServerModels.ENESS_XREF_EventsLEPartnersDTO | ServerModels.ENESS_LEPartnerDTO): any {
    let name1 = p1.FirstName;
    let name2 = p2.FirstName;

    if (name1 < name2)
      return -1;

    if (name1 > name2)
      return 1;

    return 0;
  }
}
